import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useToast } from '../../../../components/ui/Toast';
import { useAreaGroups, useCreateArea } from '../../../../features/settings/network/areas.api';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/settings/areas/new')({
  component: CreateAreaPage,
});

const areaSchema = z.object({
  name: z.string().min(1, 'Area name is required'),
  code: z.string().min(1, 'Area code is required'),
  areaGroupId: z.string().min(1, 'Area group is required'),
});

type AreaForm = z.infer<typeof areaSchema>;

const areaFormConfig = defineFormPage<AreaForm>({
  resource: 'areas',
  defaultValues: { name: '', code: '', areaGroupId: '' },
  schema: areaSchema,
});

function CreateAreaPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: groupsResponse } = useAreaGroups();
  const groups = groupsResponse?.data ?? [];
  const createAreaMutation = useCreateArea();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(areaFormConfig, { mutateAsync: (values) => createAreaMutation.mutateAsync(values) }, {
    routeId: 'settings/areas/new',
    onSuccess: () => {
      toast({ type: 'success', title: 'Success', description: 'Area added successfully.' });
      void navigate({ to: '/settings/areas' });
    },
    onError: (err: { message?: string }) => {
      toast({ type: 'error', title: 'Error', description: err.message || 'Failed to add area.' });
    },
  });

  return (
    <FormPageLayout
      title="Add Area"
      subtitle="Register a specific local region/pin-code field office under an Area Group."
      backTo="/settings/areas"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <form.Field
              name="name"
              validators={{ onChange: ({ value }: { value: unknown }) => areaSchema.shape.name.safeParse(value).success ? undefined : areaSchema.shape.name.safeParse(value).error?.issues[0]?.message }}
              children={(field: { state: { value: string & number & boolean & null; meta: unknown }; name: string; handleBlur: () => void; handleChange: (value: unknown) => void }) => (
                <FormField label="Local Area Name" required>
                  <Input
                    placeholder="e.g. Electronic City Phase 1"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="code"
              validators={{ onChange: ({ value }: { value: unknown }) => areaSchema.shape.code.safeParse(value).success ? undefined : areaSchema.shape.code.safeParse(value).error?.issues[0]?.message }}
              children={(field: { state: { value: string & number & boolean & null; meta: unknown }; name: string; handleBlur: () => void; handleChange: (value: unknown) => void }) => (
                <FormField label="Area Code (Short Name)" required>
                  <Input
                    placeholder="e.g. ECP-1"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="areaGroupId"
              validators={{ onChange: ({ value }: { value: unknown }) => areaSchema.shape.areaGroupId.safeParse(value).success ? undefined : areaSchema.shape.areaGroupId.safeParse(value).error?.issues[0]?.message }}
              children={(field: { state: { value: string & number & boolean & null; meta: unknown }; name: string; handleBlur: () => void; handleChange: (value: unknown) => void }) => (
                <FormSelect
                  label="Mapped Area Group"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                  options={groups.map((g: Record<string, unknown>) => ({
                    value: g.id as string,
                    label: `${g.name as string} (${g.code as string})`,
                  }))}
                  disabled={isSubmitting}
                />
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/settings/areas' })} isSubmitting={isSubmitting} submitLabel="Add Area" />
          </form>
    </FormPageLayout>
  );
}
